import { analytics } from "./analytics";
import { currentSource } from "./attribution";

/**
 * Tells a visitor apart from a fetch.
 *
 * `session_start` fires for anything that loads the document, including a
 * prefetch and an in-app-browser preload, so every arrival number this surface
 * has is an upper bound with an unknown amount of nothing inside it. Measured
 * over the first 7.19 days of live data, read from ClickHouse 29 Aug 2026:
 * seven devices arrived tagged `src=mk` and six tagged `src=telegram`, and ten
 * of those thirteen recorded a session lasting between 0 and 1100 ms. The
 * `src=telegram` six were 0 to 41 ms. Nobody reads a Persian article in 41
 * milliseconds.
 *
 * So the 27 Nov gate, which is written in arrivals, currently cannot separate
 * "the catalogue sent seven humans" from "it sent three humans and four
 * prefetches". This module closes that, and it has to be in place well before
 * the reading rather than alongside it.
 *
 * Two conditions, both required, because either alone is forgeable:
 *
 *   - **Five seconds of visible time.** Accumulated only while the tab is
 *     foregrounded, so a preload in a background tab never banks any of it. A
 *     prefetch that is never looked at can sit for an hour and still owe five
 *     seconds.
 *   - **One deliberate interaction.** A scroll, a pointer, a key or a touch.
 *     A prefetch produces none of these by definition, because nobody is
 *     pointing at it.
 *
 * Once per session, not once per page. The gate counts people and the event
 * has to count the same thing, or the two numbers cannot be compared. It is
 * also why this is not simply a timer inside a route component: a reader who
 * lands on `/` and clicks into an article is one engaged person, not two.
 */

const FIRED_KEY = "fa-engaged";
const REQUIRED_VISIBLE_MS = 5000;
const TICK_MS = 500;

/** Session-scoped, and a failure to read it means "not yet fired", never "fired". */
function alreadyFired(): boolean {
  try {
    return window.sessionStorage.getItem(FIRED_KEY) === "1";
  } catch {
    // Private browsing throws. Better to risk a second event in one session
    // than to lose the signal entirely, and the analysis counts devices.
    return false;
  }
}

function markFired(): void {
  try {
    window.sessionStorage.setItem(FIRED_KEY, "1");
  } catch {
    /* nothing to do; see alreadyFired */
  }
}

/**
 * Start watching. Safe to call on the server, where it does nothing, and safe
 * to call twice, where the second call returns immediately.
 */
export function startEngagementWatch(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (alreadyFired()) return;

  let visibleMs = 0;
  let interacted = false;
  let done = false;
  let timer: ReturnType<typeof setInterval> | null = null;

  const INTERACTIONS = ["scroll", "pointerdown", "keydown", "touchstart", "wheel"] as const;

  function stop(): void {
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
    for (const type of INTERACTIONS) {
      window.removeEventListener(type, onInteract);
    }
  }

  function maybeFire(): void {
    if (done) return;
    if (!interacted || visibleMs < REQUIRED_VISIBLE_MS) return;
    done = true;
    stop();
    markFired();
    // The path at the moment of engagement, not the landing path: it answers
    // "what were they actually reading", which the landing URL does not.
    analytics.engaged({ src: currentSource(), path: window.location.pathname });
  }

  function onInteract(): void {
    interacted = true;
    maybeFire();
  }

  for (const type of INTERACTIONS) {
    window.addEventListener(type, onInteract, { passive: true });
  }

  timer = setInterval(() => {
    // Only foregrounded time counts. This is the half that a prefetch fails.
    if (document.visibilityState === "visible") {
      visibleMs += TICK_MS;
      maybeFire();
    }
  }, TICK_MS);
}
